import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Mail, Pencil, ChevronDown, ChevronRight } from "lucide-react";
import { phoneHref } from "../../lib/formatters";
import { STANDARD_POSITIONS, positionLabel, hasInfo } from "../../lib/jobContacts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";

// One row per person: name, company, then the phone and email as tap-to-call / tap-to-email links.
const Person = ({ person, testId }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={testId}>
    <span className="font-medium min-w-[8rem]">{person.name || <span className="text-muted-foreground italic">No name</span>}</span>
    {person.company && <span className="text-xs text-muted-foreground" data-testid={`${testId}-company`}>{person.company}</span>}
    {person.phone && (
      <a href={phoneHref(person.phone)} title={`Call ${person.phone}`} className="font-mono text-xs text-slate-700 hover:text-sky-700 hover:underline" data-testid={`${testId}-call`}>{person.phone}</a>
    )}
    {person.email && (
      <a href={`mailto:${person.email.trim()}`} title={`Email ${person.email}`} className="text-xs text-sky-700 hover:underline truncate max-w-[16rem]" data-testid={`${testId}-email`}>{person.email}</a>
    )}
  </div>
);

const JobContactsCard = ({ job, canManageJobs }) => {
  const [open, setOpen] = useState(true);
  const contacts = job.contacts || {};
  const standardKeys = STANDARD_POSITIONS.map((p) => p.key);
  const keys = [...standardKeys, ...Object.keys(contacts).filter((k) => !standardKeys.includes(k))];
  const lines = keys
    .map((key) => {
      const primary = contacts[key] || {};
      const people = [primary, ...(primary.additional || [])].filter(hasInfo);
      const emails = people.map((p) => (p.email || "").trim()).filter((e) => e.includes("@"));
      return { key, label: positionLabel(key, primary), people, emails };
    })
    .filter((l) => l.people.length > 0);

  return (
    <Card data-testid="job-contacts-card">
      <CardHeader className="py-3 flex flex-row items-center justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left" data-testid="job-contacts-toggle">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Contacts{lines.length ? ` (${lines.reduce((n, l) => n + l.people.length, 0)})` : ""}</CardTitle>
        </button>
        {canManageJobs && (
          <Button asChild variant="ghost" size="sm" data-testid="job-contacts-edit-link">
            <Link to={`/jobs/${job.id}/edit`}><Pencil className="w-4 h-4 mr-1" /> Edit</Link>
          </Button>
        )}
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {lines.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="job-contacts-empty">No contacts on this job yet{canManageJobs ? " — add them with Edit." : "."}</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              {lines.map((l) => (
                <div key={l.key} className="space-y-1" data-testid={`job-contact-line-${l.key}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm uppercase tracking-wide text-muted-foreground font-semibold underline underline-offset-4 decoration-slate-300">{l.label}</span>
                    {l.emails.length > 1 && (
                      <a href={`mailto:${l.emails.join(",")}`} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={`job-contact-${l.key}-email-all`}>
                        <Mail className="w-3 h-3" /> Email all ({l.emails.length})
                      </a>
                    )}
                  </div>
                  {l.people.map((p, i) => <Person key={i} person={p} testId={`job-contact-${l.key}-${i}`} />)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default JobContactsCard;
